// ============================================================================
// Contracts — line item editor. Shows the line items table for a contract
// and allows add / edit / remove (RLS gates writes to creator-while-Draft
// or finance/executive/superuser at any stage).
// ============================================================================

import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button, ErrorState } from '../../shared/components';
import { toNumOrNull } from '../../shared/coerce';
import type { ContractLineItemRow, LineType } from './types';
import { LINE_TYPES, LINE_TYPE_LABELS } from './types';
import {
  createLineItem,
  deleteLineItem,
  useLineItems,
} from './hooks/use-contract-data';
import { errorMessage } from '../../shared/errors';

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

interface NewLineState {
  description: string;
  line_type: LineType;
  quantity: string;
  unit_label: string;
  unit_rate: string;
  line_total: string;
  sub_cost: string;
  notes: string;
}

const NEW_LINE_INITIAL: NewLineState = {
  description: '',
  line_type: 'Recurring_Monthly',
  quantity: '1',
  unit_label: '',
  unit_rate: '',
  line_total: '',
  sub_cost: '',
  notes: '',
};

const parseAmount = (s: string): number | null => toNumOrNull(s);

interface LineItemEditorProps {
  contractId: string;
  canEdit: boolean;
}

export function LineItemEditor({ contractId, canEdit }: LineItemEditorProps) {
  const { data, isLoading, error, refetch } = useLineItems(contractId);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewLineState>(NEW_LINE_INITIAL);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  function field<K extends keyof NewLineState>(k: K, v: NewLineState[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  async function handleAdd() {
    if (!form.description.trim()) {
      setAddError('Description is required.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await createLineItem({
        contract_id: contractId,
        description: form.description.trim(),
        line_type: form.line_type,
        quantity: parseAmount(form.quantity),
        unit_label: form.unit_label.trim() || null,
        unit_rate: parseAmount(form.unit_rate),
        line_total: parseAmount(form.line_total),
        sub_cost: parseAmount(form.sub_cost),
        notes: form.notes.trim() || null,
      });
      setForm(NEW_LINE_INITIAL);
      setShowForm(false);
      refetch();
    } catch (err) {
      setAddError(errorMessage(err, 'Failed to add line item.'));
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(line: ContractLineItemRow) {
    if (!window.confirm(`Remove line: ${line.description}?`)) return;
    setDeletingId(line.line_id);
    try {
      await deleteLineItem(line.line_id);
      refetch();
    } catch (err) {
      console.error('[contracts] delete line item failed', err);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-tcs-text">Line items</h3>
        {canEdit && !showForm && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-4 w-4" /> Add line
          </Button>
        )}
      </div>

      {error && <ErrorState title="Couldn't load line items" body={error} onRetry={refetch} />}

      {isLoading && !error && (
        <div className="text-xs text-tcs-text-muted">Loading…</div>
      )}

      {!isLoading && !error && data.length === 0 && !showForm && (
        <div className="rounded-md border border-dashed border-tcs-border bg-tcs-bg-muted px-3 py-4 text-center text-xs text-tcs-text-muted">
          No line items yet. {canEdit ? 'Add at least one before submitting for finance.' : ''}
        </div>
      )}

      {data.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-tcs-border text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
              <th className="py-2 pr-3 font-medium">Description</th>
              <th className="py-2 pr-3 font-medium">Type</th>
              <th className="py-2 pr-3 text-right font-medium">Qty</th>
              <th className="py-2 pr-3 text-right font-medium">Rate</th>
              <th className="py-2 pr-3 text-right font-medium">Line total</th>
              <th className="py-2 pr-3 text-right font-medium">Sub cost</th>
              <th className="py-2 pr-3 text-right font-medium">Margin</th>
              {canEdit && <th className="py-2 pr-3" />}
            </tr>
          </thead>
          <tbody>
            {data.map((line) => (
              <tr key={line.line_id} className="border-b border-tcs-border/40">
                <td className="py-2 pr-3 text-tcs-text">
                  <div>{line.description}</div>
                  {line.notes && (
                    <div className="text-[11px] text-tcs-text-muted">{line.notes}</div>
                  )}
                </td>
                <td className="py-2 pr-3 text-tcs-text-secondary">
                  {LINE_TYPE_LABELS[line.line_type] ?? line.line_type}
                </td>
                <td className="py-2 pr-3 text-right text-tcs-text-secondary">
                  {line.quantity ?? '—'}
                  {line.unit_label && (
                    <span className="ml-1 text-[11px] text-tcs-text-muted">
                      {line.unit_label}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-xs text-tcs-text">
                  {fmtCurrency(line.unit_rate)}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-xs text-tcs-text">
                  {fmtCurrency(line.line_total)}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-xs text-tcs-text-secondary">
                  {fmtCurrency(line.sub_cost)}
                </td>
                <td className="py-2 pr-3 text-right text-xs">
                  {line.margin_pct != null ? `${line.margin_pct}%` : '—'}
                </td>
                {canEdit && (
                  <td className="py-2 pr-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(line)}
                      disabled={deletingId === line.line_id}
                      className="text-tcs-text-muted hover:text-st-danger-fg disabled:opacity-50"
                      aria-label="Remove line"
                    >
                      {deletingId === line.line_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="mt-4 space-y-3 rounded-md border border-tcs-border bg-tcs-raised p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
            New line item
          </h4>
          {addError && <ErrorState title="Couldn't add line" body={addError} />}
          <LabeledInput
            label="Description"
            value={form.description}
            onChange={(v) => field('description', v)}
            required
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <LabeledSelect
              label="Line type"
              value={form.line_type}
              onChange={(v) => field('line_type', v as LineType)}
              options={LINE_TYPES.map((t) => ({
                value: t,
                label: LINE_TYPE_LABELS[t],
              }))}
            />
            <LabeledInput
              label="Unit label (FTE, hr/wk, sqft…)"
              value={form.unit_label}
              onChange={(v) => field('unit_label', v)}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <LabeledInput
              label="Quantity"
              type="number"
              value={form.quantity}
              onChange={(v) => field('quantity', v)}
            />
            <LabeledInput
              label="Unit rate"
              type="number"
              value={form.unit_rate}
              onChange={(v) => field('unit_rate', v)}
            />
            <LabeledInput
              label="Line total"
              type="number"
              value={form.line_total}
              onChange={(v) => field('line_total', v)}
            />
          </div>
          <LabeledInput
            label="Sub cost (our cost)"
            type="number"
            value={form.sub_cost}
            onChange={(v) => field('sub_cost', v)}
          />
          <LabeledInput
            label="Notes"
            value={form.notes}
            onChange={(v) => field('notes', v)}
          />
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAdd}
              disabled={adding}
            >
              {adding && <Loader2 className="h-4 w-4 animate-spin" />}
              {adding ? 'Adding…' : 'Add line'}
            </Button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm(NEW_LINE_INITIAL);
                setAddError(null);
              }}
              className="text-sm text-tcs-text-secondary hover:text-tcs-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  required?: boolean;
}
function LabeledInput({
  label,
  value,
  onChange,
  type = 'text',
  required,
}: LabeledInputProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
        {required && <span className="ml-0.5 text-st-danger-fg">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
      />
    </label>
  );
}

interface LabeledSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}
function LabeledSelect({ label, value, onChange, options }: LabeledSelectProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
